
import React, { useState, useEffect } from 'react';
import { COLORS } from '../constants';

interface NavbarProps {
  onOpenModal: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onOpenModal }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav 
      role="navigation"
      aria-label="Main Navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 px-4 md:px-8 flex items-center justify-between
      ${scrolled 
        ? 'py-3 bg-white/90 backdrop-blur-lg shadow-sm border-b border-gray-100' 
        : 'py-6 bg-transparent'}`}
    >
      <a href="/" className="flex items-center gap-3 cursor-pointer focus-visible:outline-emerald-500 rounded-lg p-1 group" aria-label="Novus Energy Home">
        <div className={`transition-transform duration-300 flex items-center justify-center relative -top-[1px] ${scrolled ? 'scale-90' : 'scale-100'}`}>
          <svg 
            className="w-7 h-7 md:w-8 md:h-8" 
            viewBox="0 0 24 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            style={{ color: COLORS.novusGreen }}
          >
            <path d="M12 21L11.2 11H12.8L12 21Z" fill="currentColor" opacity="0.15" />
            <path d="M12 21V11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <g style={{ animation: 'spin 6s linear infinite', transformOrigin: '12px 11px' }}>
              <circle cx="12" cy="11" r="1.2" fill="currentColor" />
              <path d="M12 11C12 11 12.4 9.5 14.5 6C15.8 3.5 14.8 2.5 13.5 2.5C12.2 2.5 12 11 12 11Z" fill="currentColor" />
              <path d="M12 11C12 11 12.4 9.5 14.5 6C15.8 3.5 14.8 2.5 13.5 2.5C12.2 2.5 12 11 12 11Z" fill="currentColor" transform="rotate(120 12 11)" />
              <path d="M12 11C12 11 12.4 9.5 14.5 6C15.8 3.5 14.8 2.5 13.5 2.5C12.2 2.5 12 11 12 11Z" fill="currentColor" transform="rotate(240 12 11)" />
            </g>
          </svg>
        </div>
        <span className={`font-bold tracking-tight transition-all duration-300 text-lg md:text-xl text-[#1E1E1E]`}>
          Novus
        </span>
      </a>

      <div className="hidden lg:flex items-center gap-10">
        {['Home', 'About Us', 'Features', 'Services', 'Contact'].map((link) => (
          <a 
            key={link} 
            href={`#${link.toLowerCase().replace(' ', '-')}`} 
            className="text-sm font-semibold transition-opacity focus-visible:outline-emerald-500 rounded px-1 text-gray-700 hover:text-black"
          >
            {link}
          </a>
        ))}
      </div>

      <div className="flex items-center gap-4">
        <button className="text-sm font-bold px-4 py-2 hover:bg-black/5 rounded-full transition-colors outline-none text-gray-900">Log in</button>
        <button 
          onClick={onOpenModal}
          className={`text-white text-sm font-bold rounded-full transition-all duration-300 active:scale-95 shadow-lg shadow-emerald-500/20 outline-none
          ${scrolled ? 'px-5 py-2' : 'px-6 py-2.5'}`}
          style={{ backgroundColor: COLORS.novusGreen }}
        >
          Get in touch
        </button>
      </div>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </nav>
  );
};

export default Navbar;